import { useMemo, useState, type ReactNode } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import Select from 'app/frontend/base/components/Select'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import Card from 'app/frontend/base/components/Card'
import { capitalize } from 'lodash'
import { Bot, Check, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { imageUrl } from 'app/frontend/base/helpers'
import { useTeam } from 'app/frontend/base/config/context/team'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { useUpdateAutoSetupSettings } from 'app/frontend/base/config/mutations'
import AutoSetupSettings, {
  AutoSetupCategory,
  RequireBuffsOption,
  requireBuffsOptionsForGameVersion,
} from 'app/frontend/types/custom/AutoSetupSettings'
import { unavailableParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import CharacterClass, {
  classColours,
} from 'app/frontend/types/custom/CharacterClass'
import { autoSetupCategoryMeta } from './autoSetupCategoryMeta'
import { useTeamEventParticipantsContext } from '../context/TeamEventParticipantsContext'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'
import { CharacterClassIcon } from 'app/frontend/base/components'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { getAllSpecializations } from 'app/frontend/base/services/characterValues'

const specializationById = getAllSpecializations()

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  event: wowaudit.Event
  activities: wowaudit.EventActivity[]
  initialActivityIds?: number[]
  onGenerate: (settings: AutoSetupSettings, activityIds: number[]) => void
  isPreviewActive?: boolean
  onDiscard?: () => void
}

const ClassName = ({ name }: { name: CharacterClass }) => (
  <span className={classColours[name].text}>{name}</span>
)

// Renders a comma-separated list of class names, each in its class colour.
const ClassList = ({ classes }: { classes: CharacterClass[] }) => (
  <>
    {classes.map((className, index) => (
      <span key={className}>
        {index > 0 ? ', ' : ''}
        <ClassName name={className} />
      </span>
    ))}
  </>
)

const requireBuffsOptionMeta: Record<
  RequireBuffsOption,
  { label: string; description: React.ReactNode }
> = {
  none: {
    label: 'No buff requirement',
    description: "Don't consider buffs at all when making selections",
  },
  major_buffs: {
    label: 'Require major buffs',
    description: (
      <ClassList
        classes={[
          'Demon Hunter',
          'Druid',
          'Mage',
          'Monk',
          'Paladin',
          'Priest',
          'Shaman',
          'Warlock',
          'Warrior',
        ]}
      />
    ),
  },
  all_buffs: {
    label: 'Require all raid buffs',
    description: (
      <>
        All classes except <ClassName name='Rogue' />,{' '}
        <ClassName name='Evoker' /> and <ClassName name='Death Knight' />
      </>
    ),
  },
  all_classes: {
    label: 'Require all classes',
    description: 'At least one character of every class',
  },
}

const AutoSetupDialog = ({
  open,
  onOpenChange,
  activities,
  initialActivityIds,
  onGenerate,
  isPreviewActive = false,
  onDiscard,
}: Props) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      className='flex max-h-[90vh] w-[90vw] max-w-[1000px] flex-col overflow-hidden'
      onOpenAutoFocus={(event) => event.preventDefault()}
      onCloseAutoFocus={(event) => event.preventDefault()}
    >
      {open ? (
        <AutoSetupDialogBody
          activities={activities}
          initialActivityIds={initialActivityIds}
          onOpenChange={onOpenChange}
          onGenerate={onGenerate}
          isPreviewActive={isPreviewActive}
          onDiscard={onDiscard}
        />
      ) : null}
    </DialogContent>
  </Dialog>
)

const AutoSetupDialogBody = ({
  activities,
  initialActivityIds,
  onOpenChange,
  onGenerate,
  isPreviewActive = false,
  onDiscard,
}: Omit<Props, 'open' | 'event'>) => {
  const { team } = useTeam()
  const {
    data: { gameVersion },
  } = usePageInfo()
  const { allEntries } = useTeamEventParticipantsContext()
  const settingsMutation = useUpdateAutoSetupSettings(team)
  const isLive = gameVersion === 'live'
  const visibleRequireBuffsOptions =
    requireBuffsOptionsForGameVersion(gameVersion)
  const visibleCategoryMeta = useMemo(
    () =>
      isLive
        ? autoSetupCategoryMeta
        : autoSetupCategoryMeta.filter((meta) => meta.category !== 'loot'),
    [isLive],
  )

  const activeActivities = useMemo(
    () => activities.filter((activity) => activity.active),
    [activities],
  )

  const [settings, setSettings] = useState<AutoSetupSettings>(
    team.autoSetupSettings,
  )
  const [selectedActivityIds, setSelectedActivityIds] = useState<Set<number>>(
    () =>
      new Set(
        initialActivityIds?.length
          ? initialActivityIds
          : activeActivities.map((activity) => activity.id),
      ),
  )

  const showEveryoneSelectedAlert = useMemo(() => {
    const selectedActivities = activeActivities.filter((activity) =>
      selectedActivityIds.has(activity.id),
    )
    if (selectedActivities.length === 0) return false
    if (selectedActivities.some((activity) => activity.maxRaidSize == null)) {
      return false
    }
    const availableCount = allEntries.filter(
      (entry) =>
        !unavailableParticipantStatuses.includes(entry.participant.status),
    ).length
    const cap = Math.min(
      ...selectedActivities.map((activity) => activity.maxRaidSize as number),
    )
    return availableCount < cap
  }, [activeActivities, allEntries, selectedActivityIds])

  const persist = (next: AutoSetupSettings) => {
    setSettings(next)
    settingsMutation.mutate(next)
  }

  const setWeightLive = (category: AutoSetupCategory, value: number) => {
    setSettings((current) => ({
      ...current,
      weights: { ...current.weights, [category]: value },
    }))
  }

  const commitWeight = (category: AutoSetupCategory, value: number) => {
    persist({
      ...settings,
      weights: { ...settings.weights, [category]: value },
    })
  }

  const requiredCharacterOptions = useMemo(() => {
    const seen = new Set<number>()
    const options: {
      value: number
      name: string
      searchValue: string
      label: ReactNode
    }[] = []
    allEntries
      .filter(
        (entry) =>
          !unavailableParticipantStatuses.includes(entry.participant.status),
      )
      .forEach((entry) => {
        const character = resolveEventParticipantCharacter(entry)
        if (seen.has(character.id)) return
        seen.add(character.id)
        const specialization = entry.specId
          ? specializationById[entry.specId.toString()]
          : undefined
        options.push({
          value: character.id,
          name: character.name,
          searchValue: character.name,
          label: (
            <span className='flex items-center gap-1.5 whitespace-nowrap'>
              {specialization ? (
                <BlizzardIcon
                  iconName={specialization.icon}
                  size='small'
                  className='shrink-0'
                />
              ) : (
                <CharacterClassIcon
                  characterClass={character.characterClass}
                  size='small'
                />
              )}
              <span className={classColours[character.characterClass]?.text}>
                {character.name}
              </span>
            </span>
          ),
        })
      })
    return options
      .sort((left, right) => left.name.localeCompare(right.name))
      .map(({ name: _name, ...option }) => option)
  }, [allEntries])

  const toggleActivity = (activityId: number) => {
    setSelectedActivityIds((current) => {
      const next = new Set(current)
      if (next.has(activityId)) {
        next.delete(activityId)
      } else {
        next.add(activityId)
      }
      return next
    })
  }

  const handleGenerate = () => {
    onGenerate(settings, Array.from(selectedActivityIds))
    onOpenChange(false)
  }

  const teamRankOptions = (team.teamRanks ?? []).map((rank) => ({
    label: <span style={{ color: rank.color ?? undefined }}>{rank.name}</span>,
    value: rank.id.toString(),
  }))

  const requireBuffsSelect = (
    <Select
      inModal
      showDescriptionInTrigger
      value={
        visibleRequireBuffsOptions.includes(settings.requireBuffs)
          ? settings.requireBuffs
          : 'all_classes'
      }
      setValue={(next) => {
        if (!next) return
        persist({
          ...settings,
          requireBuffs: next as RequireBuffsOption,
        })
      }}
      className='h-auto min-h-full px-3 py-2'
      options={visibleRequireBuffsOptions.map((option) => ({
        value: option,
        label: requireBuffsOptionMeta[option].label,
        description: requireBuffsOptionMeta[option].description,
      }))}
    />
  )

  return (
    <>
      <DialogHeader>
        <DialogTitle className='flex items-center gap-2'>
          <Bot className='h-5 w-5 text-info-foreground' />
          Auto setup
        </DialogTitle>
        <DialogDescription>
          Automatically generate encounter selections, based on your
          preferences.
        </DialogDescription>
      </DialogHeader>

      <div className='min-h-0 flex-1 space-y-6 overflow-y-auto pr-1'>
        {/* Encounter selection */}
        <div className='space-y-2'>
          <p className='text-sm font-medium'>
            {selectedActivityIds.size} encounter
            {selectedActivityIds.size === 1 ? '' : 's'} selected
          </p>
          <div className='flex flex-wrap gap-2'>
            {activeActivities.map((activity) => {
              const isSelected = selectedActivityIds.has(activity.id)
              const activityName =
                activity.encounter?.name ??
                activity.instance?.name ??
                capitalize(activity.activityType)
              return (
                <Tooltip key={activity.id}>
                  <TooltipTrigger asChild>
                    <button
                      type='button'
                      onClick={() => toggleActivity(activity.id)}
                      className={cn(
                        'w-14 overflow-hidden rounded-md border transition-colors',
                        isSelected
                          ? 'border-primary'
                          : 'border-border opacity-60 hover:opacity-100',
                      )}
                    >
                      <div className='relative'>
                        <img
                          src={
                            activity.encounter?.thumbnailUrl ??
                            activity.instance?.tileUrl ??
                            imageUrl(
                              `activity-thumbnails/${activity.activityType}.jpg`,
                            )
                          }
                          className={cn(
                            'h-12 w-14 object-cover',
                            !isSelected && 'grayscale',
                          )}
                        />
                        <div className='absolute bottom-0 right-0 rounded-tl-[6px] bg-card-nested/70 px-1 text-xs font-semibold leading-tight'>
                          {activity.difficulty?.[0].toUpperCase() ?? '-'}
                        </div>
                        {isSelected ? (
                          <div className='absolute left-0 top-0 rounded-br-[6px] bg-primary px-0.5 text-primary-foreground'>
                            <Check className='h-3 w-3' />
                          </div>
                        ) : null}
                      </div>
                      <div className='truncate px-0.5 py-0.5 text-center text-[10px] leading-tight'>
                        {activityName}
                      </div>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{activityName}</TooltipContent>
                </Tooltip>
              )
            })}
          </div>
        </div>

        {showEveryoneSelectedAlert ? (
          <Alert variant='info'>
            <AlertDescription>
              There is room for all characters in the selected encounters, so
              auto setup will simply select everyone.
            </AlertDescription>
          </Alert>
        ) : null}

        {/* Slider grid */}
        <div className='space-y-2'>
          <p className='text-muted-foreground'>
            Adjust these sliders to control what the setup generator should
            prioritise.
          </p>
          <div className='grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3'>
            {visibleCategoryMeta.map((meta) => (
              <Card
                key={meta.category}
                className='overflow-hidden p-0'
                size='sm'
              >
                <div className='flex items-center gap-1.5 border-b px-3 py-2'>
                  <span className='text-sm font-medium'>{meta.label}</span>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className='h-3.5 w-3.5 text-muted-foreground' />
                    </TooltipTrigger>
                    <TooltipContent className='max-w-xs'>
                      {meta.tooltip}
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className='px-3 py-3'>
                  <Slider
                    min={0}
                    max={1}
                    step={0.05}
                    value={[settings.weights[meta.category]]}
                    onValueChange={([value]) =>
                      setWeightLive(meta.category, value)
                    }
                    onValueCommit={([value]) =>
                      commitWeight(meta.category, value)
                    }
                  />
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Option cards */}
        <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
          <OptionCheckboxCard
            label='Allow alts / off-specs'
            description='Swap characters to their off-specs or alts when required'
            checked={settings.allowAltsOffspecs}
            onCheckedChange={(checked) =>
              persist({ ...settings, allowAltsOffspecs: checked })
            }
          />
          {isLive ? (
            <OptionCheckboxCard
              label='Prioritise raid plan validity'
              description='Select characters that are needed in linked raid plans first'
              checked={settings.prioritiseRaidPlanValidity}
              onCheckedChange={(checked) =>
                persist({
                  ...settings,
                  prioritiseRaidPlanValidity: checked,
                })
              }
            />
          ) : null}
          <Card className='flex items-start gap-3' size='sm'>
            <Checkbox
              className='mt-0.5'
              checked={settings.prioritiseNewPlayers}
              onCheckedChange={(value) =>
                persist({
                  ...settings,
                  prioritiseNewPlayers: value === true,
                })
              }
            />
            <div className='min-w-0 space-y-1'>
              <span className='text-sm font-medium'>
                Prioritise new players on rekills
              </span>
              <div className='flex items-center gap-1.5 text-xs text-muted-foreground'>
                <span>Max</span>
                <Input
                  type='number'
                  min={0}
                  className='h-5 w-10 px-1.5 py-0 text-xs'
                  value={settings.newPlayerCap ?? ''}
                  onChange={(changeEvent) =>
                    setSettings((current) => ({
                      ...current,
                      newPlayerCap:
                        changeEvent.target.value === ''
                          ? null
                          : Number(changeEvent.target.value),
                    }))
                  }
                  onBlur={() => persist(settings)}
                />
                <span>characters without a kill per encounter</span>
              </div>
            </div>
          </Card>
          {isLive ? requireBuffsSelect : null}
          <Card className='space-y-2' size='sm'>
            <span className='text-sm font-medium'>Prioritise team ranks</span>
            <p className='text-xs text-muted-foreground'>
              Select characters from these team ranks first
            </p>
            <MultiSelect<string>
              options={teamRankOptions}
              value={settings.priorityTeamRankIds.map((id) => id.toString())}
              placeholder='None'
              inModal
              onChange={(values) =>
                persist({
                  ...settings,
                  priorityTeamRankIds: values.map((value) => Number(value)),
                })
              }
            />
          </Card>
          <Card className='space-y-2' size='sm'>
            <span className='text-sm font-medium'>Prioritise characters</span>
            <p className='text-xs text-muted-foreground'>
              Select these characters for every encounter
            </p>
            <MultiSelect<number>
              options={requiredCharacterOptions}
              value={settings.requiredCharacterIds}
              placeholder='None'
              inModal
              optionWidth='fit'
              isSearchable
              onChange={(ids) =>
                persist({ ...settings, requiredCharacterIds: ids })
              }
            />
          </Card>
          {isLive ? null : requireBuffsSelect}
        </div>
      </div>

      <div className='flex justify-end gap-2 pt-2'>
        {isPreviewActive && onDiscard ? (
          <Button
            variant='outline'
            onClick={() => {
              onDiscard()
              onOpenChange(false)
            }}
          >
            Discard
          </Button>
        ) : null}
        <Button
          onClick={handleGenerate}
          disabled={selectedActivityIds.size === 0}
        >
          {isPreviewActive ? 'Regenerate' : 'Generate preview'}
        </Button>
      </div>
    </>
  )
}

type OptionCheckboxCardProps = {
  label: string
  description: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}

const OptionCheckboxCard = ({
  label,
  description,
  checked,
  onCheckedChange,
}: OptionCheckboxCardProps) => (
  <Card className='flex items-start gap-3' size='sm'>
    <Checkbox
      className='mt-0.5'
      checked={checked}
      onCheckedChange={(value) => onCheckedChange(value === true)}
    />
    <div className='space-y-1'>
      <span className='text-sm font-medium'>{label}</span>
      <p className='text-xs text-muted-foreground'>{description}</p>
    </div>
  </Card>
)

export default AutoSetupDialog
